import React, { useState } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from '../lib/supabase'

export default function SignIn() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    setErrorMessage('')

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      setStatus('error')
      setErrorMessage(error.message)
    } else {
      setStatus('sent')
    }
  }

  const handleGoogleSignIn = async () => {
    setStatus('sending')
    setErrorMessage('')

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      setStatus('error')
      setErrorMessage(error.message)
    }
    // On success, browser already redirected to Google — no further handling
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 100dvh tracks the visible viewport on iOS Safari (URL bar in/out)
        // instead of overflowing below the fold like 100vh does. iOS 15.4+
        // required; older Safari falls back to the default no-min behavior,
        // which still centers correctly via flexbox.
        minHeight: '100dvh',
        padding: 'var(--space-4)',
      }}
    >
      <div
        style={{
          maxWidth: 360,
          width: '100%',
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-8)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <h1 style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-4)' }}>
          Print Tracker
        </h1>
        <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>
          Sign in to continue.
        </p>

        {status === 'sent' ? (
          <p style={{ color: 'var(--color-success)' }}>
            Check your email for a sign-in link.
          </p>
        ) : (
          <>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={status === 'sending'}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                background: 'white',
                color: '#1a1a1a',
                border: '1px solid #dadce0',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                opacity: status === 'sending' ? 0.6 : 1,
              }}
            >
              <GoogleLogo />
              Continue with Google
            </button>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                margin: 'var(--space-4) 0',
              }}
            >
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                or
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            </div>

            <form onSubmit={handleEmailSubmit}>
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'sending'}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text)',
                  marginBottom: 'var(--space-4)',
                }}
              />
              <button
                type="submit"
                disabled={status === 'sending'}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-primary)',
                  color: 'white',
                  fontWeight: 600,
                }}
              >
                {status === 'sending' ? 'Sending…' : 'Send magic link'}
              </button>
            </form>

            {status === 'error' && errorMessage && (
              <p
                style={{
                  color: 'var(--color-danger)',
                  marginTop: 'var(--space-3)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {errorMessage}
              </p>
            )}
          </>
        )}

        {import.meta.env.DEV && <SentrySmokeTest />}
      </div>
    </div>
  )
}

// Stripped from production via the import.meta.env.DEV guard. Sits on the
// pre-auth sign-in screen so the Sentry pipeline can be verified without
// having to log in (which would redirect localhost to the prod URL since
// Supabase doesn't have localhost on its redirect allowlist).
function SentrySmokeTest() {
  const [status, setStatus] = useState<string>('')
  return (
    <div style={{ marginTop: 'var(--space-6)', paddingTop: 'var(--space-4)', borderTop: '1px dashed var(--color-border)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
        Dev — Sentry smoke test (not present in production builds)
      </div>
      <button
        type="button"
        onClick={() => {
          const eventId = Sentry.captureException(
            new Error(`Sentry smoke test ${new Date().toISOString()}`),
          )
          setStatus(`Sent: ${eventId ?? '(no event id)'}`)
        }}
        style={{
          width: '100%',
          padding: 'var(--space-3)',
          background: 'transparent',
          color: 'var(--color-danger)',
          border: '1px solid var(--color-danger)',
          borderRadius: 'var(--radius-md)',
          fontWeight: 600,
          fontSize: 'var(--font-size-sm)',
        }}
      >
        Send test error to Sentry
      </button>
      {status && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {status}
        </div>
      )}
    </div>
  )
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}
